/** @format */

import React, { FunctionComponent, useEffect, useState } from 'react';
import TableRow from '@material-ui/core/TableRow';
import PlatformMsisdnPoolNumber from '../../../interfaces/platform/PlatformMsisdnPoolNumber';
import TableBody from '@material-ui/core/TableBody';
import TableFooter from '@material-ui/core/TableFooter';
import Filter from '../../../interfaces/forms/Filter';
import Order from '../../../interfaces/forms/Order';
import { PliCbd2CollectionInterface } from '../../../interfaces/collections/PliCbd2CollectionInterface';
import { FormGeneralHelper } from '../../../helpers/FormGeneral';
import { ItemLoaders } from '../../../core/loaders/ItemLoaders';
import { useDispatch, useSelector } from 'react-redux';
import { StorageReducerAction, StorageReducerType } from '../../../modules/storage/StorageReducers';
import { makeStyles, TableCell } from '@material-ui/core';
import PagerRange from '../../../components/Adescom/PagerRange';
import adescomStyle from '../../../assets/jss/adescom/adescomStyle';
import { DateHelperTs } from '../../../helpers/DateHelperTs';
import { AcqOperatorLoader } from '../../../modules/mnp/AcqOperatorLoader';
import I18n from '../../../components/Translate/I18n';
import PliCbd2Interface from '../../../interfaces/platform/PliCbd2Interface';
import { MnpEventType } from '../../Mnp/MnpEventType';

const useStyle = makeStyles(adescomStyle);

type MsisdnMnpHistoryItemType = {
    msisdn: PlatformMsisdnPoolNumber;
    acqOperators: AcqOperatorLoader;
    filters: Array<Filter>;
    orders: Array<Order>;
};

const firstLoad = (dispatch: any, filters: Array<Filter>, orders: Array<Order>): void => {
    const itemLoader = new ItemLoaders(dispatch);
    itemLoader.getListWithCallback(
        FormGeneralHelper.getByUri(FormGeneralHelper.getPath('/pli_cbd2_cases')),
        StorageReducerAction.LOAD_STORAGE_HISTORY_MNP_LIST,
        filters,
        orders,
        (data: PliCbd2CollectionInterface): void => {
            dispatch({
                type: StorageReducerAction.LOAD_STORAGE_HISTORY_MNP_LIST,
                plicbd2collection: data,
            } as StorageReducerType);
        },
        { propagateDataToCallback: true, dispatchAfterCallback: false }
    );
};

export const MsisdnMnpHistoryItem: FunctionComponent<MsisdnMnpHistoryItemType> = ({ msisdn, filters, orders, acqOperators }): React.ReactElement => {
    const { plicbd2collection, type } = useSelector((state: { storageReducers: StorageReducerType }) => state.storageReducers);
    const [mnpHistory, setMnpHistory] = useState<PliCbd2CollectionInterface | null>(null);
    const dispatch = useDispatch();
    const classes = useStyle();

    const tableCellRoot = { root: classes.tableCellRootBordered };
    const tableRowRoot = { root: classes.tableRowRoot };
    const mnpEventType = new MnpEventType();

    useEffect(() => {
        if (type === StorageReducerAction.LOAD_STORAGE_HISTORY_MNP_LIST) {
            if (plicbd2collection) {
                setMnpHistory(plicbd2collection);
                dispatch({
                    type: StorageReducerAction.LOAD_STORAGE_HISTORY_NOP,
                });
                return;
            }
            firstLoad(dispatch, filters, orders);
        }
    }, [dispatch, filters, orders, type, plicbd2collection, mnpHistory]);

    const pagginationCallback = (uri: string): void => {
        const itemLoader = new ItemLoaders(dispatch);
        itemLoader.paginationCallWithCallback(
            uri,
            'load_mnp_storage_history_list',
            (data: PliCbd2CollectionInterface): void => {
                dispatch({
                    type: StorageReducerAction.LOAD_STORAGE_HISTORY_MNP_LIST,
                    plicbd2collection: data,
                });
            },
            { propagateDataToCallback: true, dispatchAfterCallback: false }
        );
    };

    const getOperatorName = (operatorRpt: string | null): string | null => {
        return acqOperators.getOperatorNameByRpt(operatorRpt, true);
    };

    const getEventName = (item: PliCbd2Interface, donor: boolean): React.ReactNode | null => {
        return <I18n trans={mnpEventType.getI18Name(item.eventType, donor)} />;
    };

    return (
        <React.Fragment>
            <TableBody>
                {mnpHistory &&
                    mnpHistory['hydra:member'] &&
                    mnpHistory['hydra:member'].map((item) => {
                        return (
                            <TableRow key={'pli_cbd2_key_id_' + item.id} classes={tableRowRoot} hover>
                                <TableCell classes={tableCellRoot} padding="none" align="left" style={{ paddingLeft: 0 }}>
                                    {item.caseId}
                                </TableCell>
                                <TableCell classes={tableCellRoot} padding="none" align="left">
                                    {item.platformReseller && getEventName(item, true)}
                                    {item.recipientPlatformReseller && getEventName(item, false)}
                                </TableCell>
                                <TableCell classes={tableCellRoot} padding="none" align="left">
                                    {item.searchEventDateTime && DateHelperTs.format(item.searchEventDateTime, 'YYYY-MM-DD')}
                                </TableCell>
                                <TableCell classes={tableCellRoot} padding="none" align="left">
                                    {item.donor}
                                    <div style={{ height: '20px' }}>{getOperatorName(item.donor)}</div>
                                </TableCell>
                                <TableCell classes={tableCellRoot} padding="none" align="left">
                                    {item.recipient}
                                    <div style={{ height: '20px', padding: '0px 10px 0px 10px' }}>{getOperatorName(item.recipient)}</div>
                                </TableCell>
                            </TableRow>
                        );
                    })}
            </TableBody>
            <TableFooter>
                <TableRow>
                    <TableCell colSpan={4}>
                        {mnpHistory && mnpHistory['hydra:view'] && <PagerRange callback={pagginationCallback} views={mnpHistory} />}
                    </TableCell>
                </TableRow>
            </TableFooter>
        </React.Fragment>
    );
};
